import { ChevronDown } from 'lucide-react';
import type * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, focusRing } from '@/lib/utils';

/** A multi-select quick filter, styled to sit alongside the preset chips. */
export function FilterDropdown({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  // A selected value may be missing from the options — typed by hand, or not
  // seen recently — and must still be listed so it can be unticked.
  const values = [...new Set([...selected, ...options])];
  if (values.length === 0) return null;

  const summary =
    selected.length === 0 ? null : selected.length === 1 ? selected[0] : `${selected.length}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterTrigger label={label} summary={summary} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {values.map((value) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={selected.includes(value)}
            onCheckedChange={(checked) =>
              onChange(checked ? [...selected, value] : selected.filter((v) => v !== value))
            }
            // Stay open so several values can be ticked in one go.
            onSelect={(event) => event.preventDefault()}
            className="font-mono text-xs"
          >
            {value}
          </DropdownMenuCheckboxItem>
        ))}
        {selected.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange([])}>Clear</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The pill a quick filter hangs from. Shared so that every filter above the
 * issue stream reads as one row of controls, whatever it filters on.
 */
export function FilterTrigger({
  label,
  summary,
  className,
  ...props
}: { label: string; summary?: string | null } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
        focusRing,
        summary
          ? 'border-primary bg-brand-subtle text-foreground hover:border-primary-hover'
          : 'border-border bg-card text-muted-foreground hover:border-input-hover hover:text-foreground',
        className,
      )}
      {...props}
    >
      {label}
      {summary && <span className="max-w-[12rem] truncate font-medium">: {summary}</span>}
      <ChevronDown className="size-3.5" />
    </button>
  );
}
